import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, CheckCircle2, Save, BookText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const accountTypes = [
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
  { value: 'equity', label: 'Equity' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
];

const normalSides = [
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
];

export default function AccountForm() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentWorkspace } = useWorkspace();
  const isEdit = Boolean(accountId);

  const parentCode = searchParams.get('parentCode');
  const typeFromQuery = searchParams.get('type');

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    code: parentCode ? `${parentCode}.1` : '',
    name: '',
    type: typeFromQuery || 'asset',
    normal_side: ['asset', 'expense'].includes(typeFromQuery || 'asset') ? 'debit' : 'credit',
    is_control: false,
    control_kind: '',
    is_active: true,
  });

  useEffect(() => {
    if (!isEdit || !currentWorkspace) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts`);
        if (res.ok) {
          const accounts = await res.json();
          const acc = accounts.find(a => a.account_id === accountId);
          if (!acc) {
            toast.error('Account not found');
            navigate('/accounts');
            return;
          }
          setForm({
            code: acc.code,
            name: acc.name,
            type: acc.type,
            normal_side: acc.normal_side,
            is_control: acc.is_control,
            control_kind: acc.control_kind || '',
            is_active: acc.is_active !== false,
          });
        }
      } catch {
        toast.error('Failed to load account');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, accountId, currentWorkspace, navigate]);

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Code and name are required');
      return;
    }
    if (!currentWorkspace) return;
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts/${accountId}`
        : `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts`;
      const body = isEdit
        ? { name: form.name, is_active: form.is_active }
        : { code: form.code, name: form.name, type: form.type, normal_side: form.normal_side, is_control: false };
      const res = await apiFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(isEdit ? 'Account updated' : 'Account created');
        navigate('/accounts');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to ${isEdit ? 'update' : 'create'} account`);
      }
    } catch {
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} account`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Loading account…</div>;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="account-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/accounts')} data-testid="account-form-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BookText className="w-6 h-6" /> {isEdit ? 'Edit Account' : 'New Account'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? 'Rename or deactivate an existing account.' : 'Add an account to your chart of accounts.'}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Account Code *</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={isEdit}
                placeholder="1000"
                data-testid="account-code-input"
              />
              {isEdit && <p className="text-[10px] text-muted-foreground">Code cannot be changed after creation.</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Account Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({
                  ...form,
                  type: v,
                  normal_side: ['asset', 'expense'].includes(v) ? 'debit' : 'credit',
                })}
                disabled={isEdit}
              >
                <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {accountTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Account Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Cash, Accounts Receivable, Rent Expense…"
              data-testid="account-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Normal Balance</Label>
            <Select
              value={form.normal_side}
              onValueChange={(v) => setForm({ ...form, normal_side: v })}
              disabled={isEdit}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {normalSides.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isEdit && (
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
              <div>
                <Label className="text-sm font-medium">Account Status</Label>
                <p className="text-xs text-muted-foreground">Inactive accounts cannot be used in new entries.</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(c) => setForm({ ...form, is_active: c })}
                  data-testid="account-active-switch"
                />
                <span className="text-sm">{form.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="sticky bottom-0 -mx-4 sm:mx-0 bg-card/95 backdrop-blur border-t px-4 sm:px-0 py-3 flex justify-end gap-3 z-10">
        <Button variant="outline" onClick={() => navigate('/accounts')} data-testid="account-form-cancel-btn">Cancel</Button>
        <Button onClick={handleSave} disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[160px]"
          data-testid="save-account-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (isEdit ? <Save className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />)}
          {isEdit ? 'Save Changes' : 'Create Account'}
        </Button>
      </div>
    </div>
  );
}
